import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

import FolderSharedRoundedIcon from "@mui/icons-material/FolderSharedRounded"; // File Share
import EventRoundedIcon from "@mui/icons-material/EventRounded"; // Calendar
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded"; // Help & FAQ
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

import { selectUser } from "../../features/userSlice";
import { selectRoles } from "../../features/roleSlice";
import { checkRoles } from "../Navigation/SideNav";
import { SectionHeader } from "../UI/Page";
import { favoritesCatalog } from "./favoritesCatalog";
import { pageVisual, TILE_TONES } from "./homeIcons";
import { getLeadTimes } from "../../views/Dashboard/WindowDoor/LeadTimes/api";

const cardClass =
  "app-card group flex min-h-[4.75rem] items-center gap-4 rounded-2xl border p-4 transition duration-200 hover:-translate-y-0.5 hover:border-sky-400/50";

// Page Access grants (utils/routeAccess.js) count as well as roles.
const canSee = (item, mine, granted) =>
  checkRoles(item.roles, mine, item.path, granted);

// Favorites are saved per user in this browser's localStorage (same key the
// old My Favorites section used, so existing stars carry over).
const storageKey = (user) => `homeFavorites:${user?.id || "anon"}`;

// Card description for well-known pages; anything else falls back to its
// catalog group name. Icons/tints come from homeIcons.js.
const PAGE_DESCRIPTIONS = {
  "/windowdoor/exteriororders": "Track window & door orders",
  "/windowdoor/lead-times": "Current lead times by brand",
  "/sbcf": "Truss & panel dashboard",
  "/webleads": "Track & assign web leads",
  "/forms/recognition": "Recognize a coworker",
};

const ActionCard = ({ action }) => {
  const Icon = action.icon;
  const TrailIcon = action.external ? NorthEastRoundedIcon : ArrowForwardRoundedIcon;
  const inner = (
    <>
      <span className={`app-icon-tile ${TILE_TONES[action.tone] || TILE_TONES.sky}`}>
        <Icon />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold tracking-tight">{action.label}</p>
        <p className="app-muted truncate text-sm">{action.description}</p>
      </div>
      <TrailIcon
        className="app-muted shrink-0 opacity-40 transition group-hover:translate-x-0.5 group-hover:text-[color:var(--app-accent)] group-hover:opacity-100"
        sx={{ fontSize: 18 }}
      />
    </>
  );

  if (action.onClick) {
    return (
      <button
        type="button"
        onClick={action.onClick}
        className={`${cardClass} w-full text-left`}
      >
        {inner}
      </button>
    );
  }
  if (action.external) {
    return (
      <a href={action.href} target="_blank" rel="noreferrer" className={cardClass}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={action.to} className={cardClass}>
      {inner}
    </Link>
  );
};

// One grid: three defaults everyone gets, then the user's starred pages.
const QuickAccess = ({ onOpenCalendar }) => {
  const user = useSelector(selectUser);
  const role = useSelector(selectRoles);
  const userRoles = role?.roles || [];
  const grantedPaths = role?.grantedPaths;
  const [favorites, setFavorites] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    try {
      const raw = localStorage.getItem(storageKey(user));
      const parsed = raw ? JSON.parse(raw) : [];
      setFavorites(Array.isArray(parsed) ? parsed : []);
    } catch {
      setFavorites([]);
    }
  }, [user]);

  const save = (next) => {
    setFavorites(next);
    try {
      localStorage.setItem(storageKey(user), JSON.stringify(next));
    } catch {
      // Storage full/blocked — favorites just won't persist.
    }
  };

  const toggle = (path) =>
    save(
      favorites.includes(path)
        ? favorites.filter((p) => p !== path)
        : [...favorites, path]
    );

  const visibleSections = useMemo(
    () =>
      favoritesCatalog
        .map((section) => ({
          ...section,
          items: section.items.filter((item) =>
            canSee(item, userRoles, grantedPaths)
          ),
        }))
        .filter((section) => section.items.length > 0),
    [userRoles, grantedPaths]
  );

  const byPath = useMemo(() => {
    const map = new Map();
    favoritesCatalog.forEach((section) =>
      section.items.forEach((item) =>
        map.set(item.path, { ...item, group: section.label })
      )
    );
    return map;
  }, []);

  // Help is a default card, so a starred /help would only duplicate it.
  // Vanished catalog entries and lost roles drop out silently.
  const starred = favorites
    .map((path) => byPath.get(path))
    .filter(
      (item) =>
        item && item.path !== "/help" && canSee(item, userRoles, grantedPaths)
    );

  const leadTimesStarred = starred.some(
    (item) => item.path === "/windowdoor/lead-times"
  );

  // Live summary on the lead-times card, only fetched when it's starred.
  const [leadTimesSummary, setLeadTimesSummary] = useState(null);
  useEffect(() => {
    if (!leadTimesStarred) return;
    let cancelled = false;
    getLeadTimes()
      .then((data) => {
        if (cancelled || !Array.isArray(data) || data.length === 0) return;
        const latest = data
          .map((brand) => brand.effectiveDate)
          .filter(Boolean)
          .sort()
          .pop();
        if (!latest) return;
        const [y, m, d] = latest.split("-").map(Number);
        const pretty = new Date(y, m - 1, d).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        });
        setLeadTimesSummary(
          `Updated ${pretty} · ${data.length} brand${data.length === 1 ? "" : "s"}`
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [leadTimesStarred]);

  const defaultActions = [
    {
      label: "File Share",
      description: "Send & receive large files",
      icon: FolderSharedRoundedIcon,
      tone: "sky",
      external: true,
      href: "https://share.bigclumber.com/",
    },
    {
      label: "Calendar",
      description: "View your schedule",
      icon: EventRoundedIcon,
      tone: "amber",
      onClick: onOpenCalendar,
    },
    {
      label: "Help & FAQ",
      description: "Search how-to answers",
      icon: HelpOutlineRoundedIcon,
      tone: "rose",
      to: "/help",
    },
  ];

  const favoriteActions = starred.map((item) => {
    const visual = pageVisual(item.path, item.group);
    return {
      label: item.name,
      description:
        item.path === "/windowdoor/lead-times" && leadTimesSummary
          ? leadTimesSummary
          : PAGE_DESCRIPTIONS[item.path] || item.group,
      icon: visual.icon,
      tone: visual.tone,
      to: item.path,
    };
  });

  return (
    <section aria-labelledby="quick-access-title">
      <SectionHeader
        title={<span id="quick-access-title">Quick access</span>}
        description="Everyday shortcuts, plus the pages you've starred."
        actions={
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="app-btn app-btn-ghost app-btn-sm"
          >
            <TuneRoundedIcon sx={{ fontSize: 17 }} />
            Edit
          </button>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
        {[...defaultActions, ...favoriteActions].map((action) => (
          <ActionCard key={action.label} action={action} />
        ))}
        {favoriteActions.length === 0 && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="app-muted group flex min-h-[4.75rem] items-center justify-center gap-2 rounded-2xl border border-dashed border-[color:var(--app-input-border)] p-4 text-sm font-medium transition hover:border-amber-400 hover:bg-[color:var(--app-hover)] hover:text-[color:var(--app-text)]"
          >
            <StarBorderRoundedIcon
              className="transition group-hover:text-amber-500"
              sx={{ fontSize: 20 }}
            />
            Star your favorite pages to add them here
          </button>
        )}
      </div>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={() => setOpen(false)}
        scroll="paper"
        aria-labelledby="favorites-editor-title"
      >
        <DialogTitle
          id="favorites-editor-title"
          className="flex items-center justify-between gap-3"
          sx={{ pb: 1 }}
        >
          Choose favorites
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close favorites editor"
            className="app-icon-btn -mr-2 h-9 w-9"
          >
            <CloseRoundedIcon fontSize="small" />
          </button>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <p className="app-muted mb-5 text-sm leading-relaxed">
            Starred pages appear as cards under Quick access, next to the
            defaults everyone gets. Favorites are saved on this browser.
          </p>
          <div className="space-y-5">
            {visibleSections.map((section) => (
              <div key={section.label}>
                <p className="app-eyebrow mb-1.5 px-1">{section.label}</p>
                <div className="app-surface divide-y divide-[color:var(--app-divider)] overflow-hidden rounded-2xl border">
                  {section.items.map((item) => {
                    const active = favorites.includes(item.path);
                    return (
                      <button
                        key={item.path}
                        type="button"
                        onClick={() => toggle(item.path)}
                        aria-pressed={active}
                        className="flex min-h-[2.75rem] w-full items-center justify-between gap-3 px-3.5 py-2 text-left text-sm transition-colors hover:bg-[color:var(--app-hover)]"
                      >
                        <span className={`truncate ${active ? "font-semibold" : ""}`}>
                          {item.name}
                        </span>
                        {active ? (
                          <StarRoundedIcon
                            sx={{ fontSize: 22 }}
                            className="shrink-0 text-amber-500 dark:text-amber-400"
                          />
                        ) : (
                          <StarBorderRoundedIcon
                            sx={{ fontSize: 22 }}
                            className="app-muted shrink-0"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <span className="app-muted mr-auto text-xs">
            {starred.length} starred
          </span>
          <Button variant="contained" onClick={() => setOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default QuickAccess;
